import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import DashboardShell from "@/components/DashboardShell";
import { Button } from "@/components/ui/button";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { Check, Loader2, Zap, ArrowUp, ArrowDown, Minus } from "lucide-react";

const COMPARE_TIERS = [
  { id: "starter", name: "Starter" },
  { id: "growth", name: "Growth", featured: true },
  { id: "pro", name: "Pro" },
];
const COMPARE_ROWS = [
  { label: "AI Content Studio", starter: true, growth: true, pro: true },
  { label: "Smart scheduling calendar", starter: true, growth: true, pro: true },
  { label: "Milestones, streaks & rewards", starter: true, growth: true, pro: true },
  { label: "Platforms tracked", starter: "1", growth: "All 4", pro: "All 4" },
  { label: "Weekly growth recap email", starter: false, growth: true, pro: true },
  { label: "Amplify — creator shoutouts", starter: false, growth: true, pro: true },
  { label: "Amplify submissions / month", starter: "—", growth: "4", pro: "15" },
  { label: "Real YouTube creator discovery", starter: false, growth: true, pro: true },
  { label: "Priority support", starter: "Standard", growth: "Priority", pro: "Priority+" },
];

const CompareCell = ({ v }) => {
  if (v === true) return <Check className="w-4 h-4 text-primary mx-auto" />;
  if (v === false) return <Minus className="w-4 h-4 text-muted-foreground/40 mx-auto" />;
  return <span className="text-sm font-semibold">{v}</span>;
};

const Plans = () => {
  const { user, refreshUser } = useAuth();
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [checkingOut, setCheckingOut] = useState(null);
  const [canceling, setCanceling] = useState(false);
  const [cycle, setCycle] = useState("monthly");

  useEffect(() => {
    api.get("/plans").then((r) => setPlans(r.data.plans)).catch(() => {}).finally(() => setLoading(false));
  }, []);

  const yearly = cycle === "yearly";
  const priceOf = (p) => (yearly ? p.price_yearly : p.price);

  const checkout = async (plan) => {
    setCheckingOut(plan.id);
    try {
      const lookup = yearly ? plan.lookup_key_yearly : plan.lookup_key;
      // Existing subscribers change plan in place (prorated); new users go through Stripe Checkout.
      if (user?.subscription_status === "active") {
        await api.post("/subscription/change", { lookup_key: lookup, origin_url: window.location.origin });
        await refreshUser();
        toast.success(`You're now on the ${plan.name} plan.`);
        setCheckingOut(null);
        return;
      }
      const { data } = await api.post("/payments/checkout", { lookup_key: lookup, origin_url: window.location.origin });
      window.location.href = data.checkout_url;
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Checkout unavailable. Please try again shortly.");
      setCheckingOut(null);
    }
  };

  const cancel = async () => {
    setCanceling(true);
    try {
      await api.post("/subscription/cancel");
      await refreshUser();
      toast.success("Cancellation scheduled — you keep access until your period ends. Check your inbox 💙");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Could not cancel");
    } finally {
      setCanceling(false);
    }
  };

  const isActive = user?.subscription_status === "active";
  const currentPrice = plans.find((p) => p.id === user?.plan)?.price;
  const currentPlan = plans.find((p) => p.id === user?.plan);

  const ctaLabel = (p) => {
    if (user?.plan === p.id && isActive) return "Current plan";
    if (isActive && currentPrice != null) {
      if (p.price > currentPrice) return "upgrade";
      if (p.price < currentPrice) return "downgrade";
    }
    return "get";
  };

  return (
    <DashboardShell>
      <div className="p-6 md:p-10 max-w-6xl mx-auto">
        <div className="mb-8">
          <h1 className="font-heading text-3xl font-black tracking-tight">Choose your growth plan</h1>
          <p className="text-muted-foreground mt-2">Cancel anytime · 7-day money-back guarantee. Your campaign launches instantly after checkout.</p>
        </div>

        <div className="mb-10 inline-flex items-center gap-1 rounded-full border border-white/10 bg-card p-1" data-testid="billing-cycle-toggle">
          <button onClick={() => setCycle("monthly")} data-testid="cycle-monthly"
            className={`px-5 py-2 rounded-full text-sm font-semibold transition-colors ${!yearly ? "bg-primary text-background" : "text-muted-foreground hover:text-foreground"}`}>Monthly</button>
          <button onClick={() => setCycle("yearly")} data-testid="cycle-yearly"
            className={`px-5 py-2 rounded-full text-sm font-semibold transition-colors flex items-center gap-2 ${yearly ? "bg-primary text-background" : "text-muted-foreground hover:text-foreground"}`}>
            Yearly <span className={`text-[11px] font-bold px-1.5 py-0.5 rounded-full ${yearly ? "bg-background/20" : "bg-primary/15 text-primary"}`}>2 months free</span>
          </button>
        </div>

        {isActive && currentPlan && (
          <div className="mb-8 rounded-2xl border border-primary/30 bg-primary/5 p-6 flex flex-wrap items-center justify-between gap-4" data-testid="current-sub-banner">
            <div>
              <div className="text-xs uppercase tracking-widest text-primary font-bold mb-1">Current subscription</div>
              <div className="font-heading text-2xl font-black">{currentPlan.name} · ${currentPlan.price}/mo</div>
              <div className="text-sm text-muted-foreground mt-1">Renews monthly. Upgrade or downgrade below, or cancel anytime.</div>
            </div>
            <AlertDialog>
              <AlertDialogTrigger asChild>
                <Button variant="outline" className="rounded-full border-white/15 text-foreground" data-testid="cancel-sub-btn" disabled={canceling}>
                  {canceling ? <Loader2 className="w-4 h-4 animate-spin" /> : "Cancel subscription"}
                </Button>
              </AlertDialogTrigger>
              <AlertDialogContent className="bg-card border-border">
                <AlertDialogHeader>
                  <AlertDialogTitle>Cancel your subscription?</AlertDialogTitle>
                  <AlertDialogDescription>Your growth campaign will be paused and analytics will stop updating. You can resubscribe anytime.</AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel className="rounded-full">Keep plan</AlertDialogCancel>
                  <AlertDialogAction onClick={cancel} className="rounded-full bg-secondary text-white hover:bg-secondary/90" data-testid="confirm-cancel-btn">Yes, cancel</AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          </div>
        )}

        {loading ? (
          <div className="flex justify-center py-20"><Loader2 className="w-8 h-8 text-primary animate-spin" /></div>
        ) : (
          <div className="grid md:grid-cols-3 gap-6 items-start">
            {plans.map((p, i) => {
              const isCurrent = user?.plan === p.id && user?.subscription_status === "active";
              const featured = p.id === "growth";
              return (
                <motion.div key={p.id} initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.08 }}
                  className={`rounded-2xl p-8 border ${featured ? "border-primary/40 bg-card shadow-[0_0_30px_rgba(0,242,254,0.12)]" : "border-border/50 bg-card"}`} data-testid={`plan-card-${p.id}`}>
                  {featured && <div className="inline-flex items-center gap-2 mb-4"><span className="px-3 py-1 rounded-full bg-primary text-background text-xs font-bold uppercase tracking-wider">Most popular</span>{yearly && <span className="px-3 py-1 rounded-full bg-chart-4 text-white text-xs font-bold uppercase tracking-wider" data-testid="best-value-ribbon">Best value</span>}</div>}
                  <h3 className="font-heading text-2xl font-bold">{p.name}</h3>
                  <p className="text-sm text-muted-foreground mb-5">{p.tagline}</p>
                  <div className="flex items-end gap-1 mb-1">
                    <span className="font-heading text-5xl font-black">${priceOf(p)}</span>
                    <span className="text-muted-foreground mb-1.5">/{yearly ? "yr" : "mo"}</span>
                    {yearly && (
                      <span className="mb-2 ml-1 text-[11px] font-bold text-primary bg-primary/15 border border-primary/30 rounded-full px-2 py-0.5" data-testid={`yearly-savings-${p.id}`}>
                        Save ${p.price * 12 - p.price_yearly}/yr
                      </span>
                    )}
                  </div>
                  <div className="text-xs text-muted-foreground mb-5 h-4">{yearly ? <><span className="line-through mr-1">${p.price * 12}/yr</span> just ${(p.price_yearly / 12).toFixed(2)}/mo</> : ""}</div>
                  <Button disabled={isCurrent || checkingOut === p.id} onClick={() => checkout(p)} data-testid={`plan-checkout-${p.id}`}
                    className={`w-full rounded-full font-bold ${featured ? "bg-primary text-background hover:bg-primary/90" : "bg-white/5 text-foreground border border-white/15 hover:bg-white/10"} transition-colors`}>
                    {checkingOut === p.id ? <Loader2 className="w-4 h-4 animate-spin" /> : isCurrent ? "Current plan"
                      : ctaLabel(p) === "upgrade" ? <>Upgrade <ArrowUp className="w-4 h-4 ml-1" /></>
                      : ctaLabel(p) === "downgrade" ? <>Downgrade <ArrowDown className="w-4 h-4 ml-1" /></>
                      : <>Get {p.name} <Zap className="w-4 h-4 ml-1" fill="currentColor" /></>}
                  </Button>
                  <ul className="mt-7 space-y-3">
                    {p.features.map((f) => (
                      <li key={f} className="flex items-start gap-3 text-sm">
                        <Check className="w-4 h-4 text-primary mt-0.5 shrink-0" /> <span className="text-muted-foreground">{f}</span>
                      </li>
                    ))}
                  </ul>
                </motion.div>
              );
            })}
          </div>
        )}

        {!loading && (
          <div className="mt-16" data-testid="plan-compare">
            <h2 className="font-heading text-2xl font-black tracking-tight mb-1">Compare plans</h2>
            <p className="text-muted-foreground text-sm mb-6">See exactly what unlocks as you grow — including Amplify creator shoutouts.</p>
            <div className="overflow-x-auto rounded-2xl border border-border/50 bg-card">
              <table className="w-full min-w-[560px] text-left">
                <thead>
                  <tr className="border-b border-white/10">
                    <th className="p-4 text-sm font-semibold text-muted-foreground">Feature</th>
                    {COMPARE_TIERS.map((t) => (
                      <th key={t.id} className={`p-4 text-center ${t.featured ? "bg-primary/5" : ""}`}>
                        <span className="font-heading font-bold">{t.name}</span>
                        {t.featured && <span className="ml-2 text-[10px] font-bold uppercase tracking-wider text-primary">Popular</span>}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {COMPARE_ROWS.map((row, i) => (
                    <tr key={row.label} className={i % 2 ? "bg-white/[0.02]" : ""} data-testid={`compare-row-${i}`}>
                      <td className="p-4 text-sm text-muted-foreground">{row.label}</td>
                      <td className="p-4 text-center">{<CompareCell v={row.starter} />}</td>
                      <td className="p-4 text-center bg-primary/5">{<CompareCell v={row.growth} />}</td>
                      <td className="p-4 text-center">{<CompareCell v={row.pro} />}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {isActive && user?.plan !== "pro" && (
              <div className="mt-6 flex justify-center">
                <Button onClick={() => checkout(plans.find((p) => p.id === "pro"))} disabled={checkingOut === "pro"}
                  className="rounded-full bg-primary text-background font-bold hover:bg-primary/90 hover:scale-105 transition-transform px-8" data-testid="compare-upgrade-pro">
                  {checkingOut === "pro" ? <Loader2 className="w-4 h-4 animate-spin" /> : <>Upgrade to Pro <ArrowUp className="w-4 h-4 ml-1" /></>}
                </Button>
              </div>
            )}
          </div>
        )}
      </div>
    </DashboardShell>
  );
};

export default Plans;
